import type AssetDocumentStore from 'stores/Asset';
import { DynamicPropertyType } from 'types/DynamicPropertyType';

import { getOwningCreative, makeCSVImportError } from '../tree';
import type DynamicGroup from './Group';
import { DynamicProperty } from './Property';

export default class Asset extends DynamicProperty<string> {
  type = DynamicPropertyType.ASSET;
  value = '';

  constructor(parent: DynamicGroup, name: string, value: string) {
    super(parent, name, value);
    // Not redundant: the `value = ''` initializer above runs after super()
    // and clobbers what it assigned. Group.create puts back any non-null
    // value afterwards, but not null - which is what adding a property from
    // the UI passes - so this is what keeps a new Asset's value equal to its
    // initialValue.
    this.value = value;
    // It's not clear why all of these DynamicProperty subclasses have
    // becomeObservable (formerly init) commented out. This was done in
    // commit bc6c93d, but the message only describes the what and not the
    // why:
    // ```
    // adding more state management features to dynamic properties.
    // removing mobx state from initial population of dynamic properties.
    // ```
    //
    // this.becomeObservable();
  }

  get label() {
    return this.assetPath ?? '';
  }

  getString = () => {
    return this.assetURL ?? '';
  };

  getPreviewData = () => {
    return this.assetURL ?? '';
  };

  setUsingCSV = (value: string) => {
    // No owning creative falls into the same error as a name that matches
    // nothing, which is the right answer for a CSV import: either way this
    // value cannot be resolved to an asset.
    const asset = getOwningCreative(this)?.assets.getByPath(value);
    if (!asset) {
      throw makeCSVImportError(
        this,
        `Invalid or missing asset: ${JSON.stringify(value)}`
      );
    }
    this.set(asset.id);
  };

  getCSVData = () => {
    return this.assetPath ?? '';
  };

  private get assetStore(): AssetDocumentStore | undefined {
    return getOwningCreative(this)?.assets.getById(this.value);
  }

  private get assetPath() {
    return this.assetStore?.data.path;
  }

  public get assetURL() {
    return this.assetStore?.downloadURL;
  }
}
